import i18n from "@/i18n.ts";
import { ApiError, ApiErrorException, ApiErrors, ApiValidationError } from "@/types/api.types.ts";
import { useToasts } from "@skailer-com/uikit";
import { Control, FieldValues, Path } from "react-hook-form";

function getErrorMessage(code: string, userMessage: string | undefined) {
  const key = `api_errors:${code}`;
  return i18n.exists(key) ? i18n.t(key) : (userMessage ?? i18n.t(key));
}

export default function useErrorHandle() {
  const serverErrorField = "root.serverError";

  const { showError } = useToasts();

  function handleErrors<
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown,
    TTransformedValues = TFieldValues,
  >(
    error: ApiError | undefined,
    control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
    fieldMap: Record<string, string> = {}
  ) {
    if (error) {
      if (error.status == ApiErrors.validationError) {
        const validationError = error as ApiValidationError;
        if (control) {
          validationError.validationErrors.forEach(({ field: serverField, code, user_message }) => {
            const isMapped = Object.hasOwn(fieldMap, serverField);
            const field = isMapped ? fieldMap[serverField] : serverField;
            const fieldParts = field.split(".");
            let fieldState: unknown = control._fields;
            for (const part of fieldParts) {
              fieldState = (fieldState as Record<string, unknown>)?.[part];
            }

            const errorMessage = getErrorMessage(code, user_message);
            const errorField = isMapped || fieldState != undefined ? field : serverErrorField;

            control.setError(errorField as Path<TFieldValues>, {
              type: "custom",
              message: errorMessage,
            });
          });
        } else {
          if (validationError.validationErrors.length > 0) {
            const { code, user_message } = validationError.validationErrors[0];
            showError({ title: getErrorMessage(code, user_message) });
          }
        }
      } else {
        // todo show toast
      }
    } else {
      // todo show toast
    }
  }

  function onError<
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown,
    TTransformedValues = TFieldValues,
  >(
    error: Error,
    control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined,
    fieldMap: Record<string, string> = {}
  ) {
    if (error instanceof ApiErrorException) {
      handleErrors((error as ApiErrorException).apiError, control, fieldMap);
    } else {
      // todo process another errors
    }
  }

  return {
    serverErrorField,
    handleErrors,
    onError,
  };
}
